import * as React from 'react'
import { lazyDocument, useEventListener, useUniqueId } from '@ds/react-utils'
/**
 * Adds aria-expanded=["true"|"false"] to the triggerElement
 * based on the value of 'expanded'.
 */
export function useAriaExpanded(args: {
  enabled?: boolean
  expanded: boolean
  triggerElement?: HTMLElement | null
}) {
  const { enabled = true, expanded, triggerElement } = args

  React.useEffect(() => {
    let trigger: HTMLElement | undefined

    if (enabled && triggerElement instanceof HTMLElement) {
      trigger = triggerElement
      trigger.setAttribute('aria-expanded', expanded ? 'true' : 'false')
    }
    return () => {
      if (enabled && trigger && trigger.hasAttribute('aria-expanded')) {
        trigger.removeAttribute('aria-expanded')
      }
    }
  }, [enabled, expanded, triggerElement])
}

/**
 * Adds aria-controls=<IDREF> to the controlling element.
 *
 * If the controlledElement has an existing id attribute that value
 * will be used as the IDREF.
 *
 * If there is not an existing `id` attribute the hook will use the
 * passed `id`, or generate a unique `id` if undefined.
 */
export function useAriaControls(args: {
  controlledElement?: HTMLElement | null
  controllingElement?: HTMLElement | null
  enabled?: boolean
  id?: string
}) {
  const { enabled = true, controlledElement, controllingElement, id } = args
  const uniqueId = useUniqueId('short')
  const userSuppliedOrGeneratedId = id ?? uniqueId

  React.useEffect(() => {
    let existingId: string | null
    if (
      enabled &&
      controlledElement instanceof HTMLElement &&
      controllingElement instanceof HTMLElement
    ) {
      existingId = controlledElement.getAttribute('id')

      const idToUse = existingId ?? userSuppliedOrGeneratedId

      controlledElement.setAttribute('id', idToUse)
      controllingElement.setAttribute('aria-controls', idToUse)
    }
    return () => {
      if (enabled) {
        controllingElement?.removeAttribute('aria-controls')
        if (!existingId) {
          // if the id was there before this hook set it don't remove it
          controlledElement?.removeAttribute('id')
        }
      }
    }
  }, [
    controlledElement,
    controllingElement,
    enabled,
    userSuppliedOrGeneratedId,
  ])
}

/**
 * When a 'focusin' event is detected on the document and the element
 * receiving focus is not contained by one of the elements specified by
 * args.elements, invoke the event handler.
 *
 * In simple terms call the handler whenever focus leaves the elements
 * in the elements array.
 *
 * Caveat: Focus can leave the elements without triggering this handler
 * when a user clicks a non-focusable element.  To handle this scenario
 * leverage useOutsideClickListener.
 */
export function useOutsideFocusListener(args: {
  capture?: boolean
  elements?: Array<HTMLElement | null | undefined>
  enabled: boolean
  handler: (event: Event) => void
}) {
  useOutsideElements({ eventType: 'focusin', ...args })
}

// This is based on BaseCalloutWithState's version leveraging mouseup
export function useOutsideClickListener(args: {
  capture?: boolean
  elements?: Array<HTMLElement | null | undefined>
  enabled: boolean
  handler: (event: Event) => void
}) {
  useOutsideElements({ eventType: 'mouseup', ...args })
}

/**
 * Generalized hook to execute a callback when event.target is NOT within
 * the supplied elements.  Restricting eventType to mouseup & focusin
 * because they are confirmed to work when checking against event.target.
 */
function useOutsideElements(args: {
  capture?: boolean
  elements?: Array<HTMLElement | null | undefined>
  enabled: boolean
  eventType: 'focusin' | 'mouseup'
  handler: (event: Event) => void
}) {
  const callbackRef = React.useRef<(event: Event) => void | undefined>()
  callbackRef.current = args.handler

  // Assign to a ref to avoid unnecessary add/remove of the event listener
  const containersRef = React.useRef<Array<HTMLElement | null | undefined>>()
  containersRef.current = args.elements

  const handler = React.useCallback((event: Event) => {
    /**
     * focusin: target is the element receiving focus
     * mouseup: target is the clicked element
     *
     * Be careful with the events you consider for this hook, you
     * may want relatedTarget depending on the event type.
     */
    const target = event.target instanceof Node ? event.target : null

    // Remove anything that's not an HTML element
    const containers = containersRef.current?.filter<HTMLElement>(
      (val: unknown): val is HTMLElement => {
        return val instanceof HTMLElement
      },
    )

    if (
      // If we have elements, and something to execute...
      containers &&
      containers.length > 0 &&
      callbackRef.current &&
      target
    ) {
      const targetIsInContainer = containers.some((container) =>
        container.contains(target),
      )

      if (!targetIsInContainer) {
        callbackRef.current(event)
      }
    }
  }, [])

  useEventListener(
    args.eventType,
    handler,
    lazyDocument,
    args.enabled ?? false,
    args.capture,
  )
}
